import { FC } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import { NextSeo } from 'next-seo';
import type { Project } from '@lib/types/project';
import ProjectSlider from '@components/project/ProjectSlider';
import ProjectTag from '../ProjectTag';
import ProjectSidebar from '../ProjectSidebar';
import styles from './ProjectView.module.css';
import { urlFor } from '@lib/sanity';

const ProjectCard = dynamic(() => import('@components/project/ProjectCard'));

interface ProjectViewProps {
  project: Project;
  otherProjects: Project[];
}

const ProjectView: FC<ProjectViewProps> = ({ project, otherProjects }) => {
  return (
    <>
      <NextSeo
        title={`${project?.name} installed in Maricopa, Casa Grande, Chandler and Ahwatukee Arizona`}
        description={project?.description}
        canonical={`https://www.hlbelectric.com/projects/${project?.slug}`}
        openGraph={{
          type: 'website',
          title: `${project?.name} installed in Maricopa, Casa Grande, Chandler and Ahwatukee Arizona | HLB Electric`,
          description: project?.description,
          url: `https://www.hlbelectric.com/projects/${project.slug}`,
          images: [
            {
              url: urlFor(project?.images[0]).width(800).height(600).url()!,
              width: 800,
              height: 600,
              alt: project?.name,
            },
          ],
        }}
      />
      <div className="w-full max-w-none">
        <div className={cn(styles.root, 'fit')}>
          <div className={cn(styles.main, 'fit')}>
            <ProjectTag name={project.name} fontSize={32} />
            <div className={styles.sliderContainer}>
              <ProjectSlider key={project._id}>
                {project.images.map((image, i) => (
                  <div
                    key={urlFor(image).url()}
                    className={styles.imageContainer}
                  >
                    <Image
                      className={styles.img}
                      src={urlFor(image).width(600).height(600).url()}
                      alt={image.alt}
                      width={600}
                      height={600}
                      priority={i === 0}
                      quality="85"
                    />
                  </div>
                ))}
              </ProjectSlider>
            </div>
          </div>
          <ProjectSidebar project={project} className={styles.sidebar} />
        </div>
        <section className="mb-10 py-12 px-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">
            Other Projects
          </h2>
          <div className={styles.relatedProjectsGrid}>
            {otherProjects.map((p) => (
              <div key={p.slug} className="animated fadeIn">
                <ProjectCard
                  project={p}
                  key={p.slug}
                  noNameTag
                  className="animated fadeIn"
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default ProjectView;
